import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import employeeSelfService from '../../../services/employeeSelfService';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { updateEmployeeProfileClient } from '../../../utils/employeeSelfServiceClient';

const ProfileSection = ({ onNotify }) => {
  const { employee, deployment, syncNow, clientSideMode } = useEmployeeSelfService();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    phone: '',
    address: '',
    emergency_contact: '',
    bank_name: '',
    bank_account: '',
    ifsc_code: '',
  });

  const startEdit = () => {
    setForm({
      phone: employee?.phone || '',
      address: employee?.address || '',
      emergency_contact: employee?.emergency_contact || '',
      bank_name: employee?.bank_name || '',
      bank_account: employee?.bank_account || employee?.bank_account_number || '',
      ifsc_code: employee?.ifsc_code || employee?.bank_ifsc || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = clientSideMode
        ? await updateEmployeeProfileClient(employee.id, form)
        : await employeeSelfService.updateProfile(form);
      if (result.success) {
        onNotify?.({ message: 'Profile updated', type: 'success' });
        setEditing(false);
        await syncNow();
      } else {
        onNotify?.({ message: result.error || 'Update failed', type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, value, name, editable = true }) => (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      {editing && editable ? (
        <input
          className="w-full border rounded px-3 py-2 mt-1 text-sm"
          value={form[name] || ''}
          onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        />
      ) : (
        <p className="text-sm font-medium mt-1">{value || '-'}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">My Profile</h2>
          <p className="text-sm text-muted-foreground">View and update contact & bank details</p>
        </div>
        {!editing ? (
          <Button variant="outline" iconName="Edit" onClick={startEdit}>Edit</Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </div>

      <div className="bg-card border rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Employee ID" value={employee?.employee_id} editable={false} />
        <Field label="Full Name" value={employee?.full_name} editable={false} />
        <Field label="Email" value={employee?.email} editable={false} />
        <Field label="Department" value={employee?.department} editable={false} />
        <Field label="Position" value={employee?.position} editable={false} />
        <Field label="Phone" name="phone" value={employee?.phone} />
        <Field label="Address" name="address" value={employee?.address} />
        <Field label="Emergency Contact" name="emergency_contact" value={employee?.emergency_contact} />
        <Field label="Bank Name" name="bank_name" value={employee?.bank_name} />
        <Field label="Account Number" name="bank_account" value={employee?.bank_account || employee?.bank_account_number} />
        <Field label="IFSC" name="ifsc_code" value={employee?.ifsc_code || employee?.bank_ifsc} />
      </div>

      {deployment && (
        <div className="bg-card border rounded-lg p-6">
          <h3 className="font-semibold mb-3">Current Deployment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Client:</span> {deployment.client_name}</div>
            <div><span className="text-muted-foreground">Site:</span> {deployment.site_name}</div>
            <div><span className="text-muted-foreground">Shift:</span> {deployment.shift_name || '-'}</div>
            <div><span className="text-muted-foreground">Timing:</span> {deployment.shift_start || '-'} — {deployment.shift_end || '-'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSection;
